import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Typography,
  Chip,
  Paper,
  Collapse,
  Button,
  IconButton,
  Menu,
  MenuItem,
} from "@mui/material";
import ResourcesIcon from "@mui/icons-material/Handshake";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import DeleteIcon from "@mui/icons-material/Delete";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import GlassCard from "../../../components/common/GlassCard";

function ResourcesCard({ resources, onAddResource, onDeleteResource }) {
  const [expandAll, setExpandAll] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [activeId, setActiveId] = useState(null);

  const closeMenu = () => {
    setMenuAnchor(null);
    setActiveId(null);
  };

  const renderMenuButton = (resource) =>
    onDeleteResource && (
      <IconButton
        size="small"
        aria-label="Resource options"
        onClick={(e) => {
          setMenuAnchor(e.currentTarget);
          setActiveId(resource.id);
        }}
      >
        <MoreVertIcon fontSize="small" />
      </IconButton>
    );

  const sortedResources = resources
    ? [...resources].sort((a, b) => {
        const dateA = new Date(a.provided_date || 0);
        const dateB = new Date(b.provided_date || 0);
        return dateB - dateA;
      })
    : [];
  return (
    <GlassCard
      title="Resources Provided"
      addLabel="Add Resource"
      icon={<ResourcesIcon color="success" />}
      onAdd={onAddResource}
    >
      {resources?.length ? (
        <>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 2 }}>
            {resources.slice(0, 3).map((resource) => (
              <Paper
                key={resource.id}
                sx={{
                  p: 1.25,
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1,
                }}
              >
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={0.5}>
                  <Box flex={1}>
                    <Typography variant="body2" fontWeight={600}>
                      {resource.organization || "Resource"} • {resource.resource_type ? resource.resource_type.replace("_", " ") : "N/A"}
                    </Typography>
                  </Box>
                  <Box display="flex" gap={0.5} alignItems="center">
                    <Chip
                      label={resource.status || "unknown"}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                    {renderMenuButton(resource)}
                  </Box>
                </Box>
                {resource.description && (
                  <Typography variant="caption" color="text.secondary" display="block">
                    {resource.description.substring(0, 80)}{resource.description.length > 80 ? "..." : ""}
                  </Typography>
                )}
                {resource.provided_date && (
                  <Typography variant="caption" color="text.secondary" display="block">
                    {new Date(resource.provided_date).toLocaleDateString()}
                  </Typography>
                )}
              </Paper>
            ))}
          </Box>

          {sortedResources?.length > 2 && (
            <>
              <Collapse in={expandAll} timeout="auto" unmountOnExit>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 2 }}>
                  {resources.slice(3).map((resource) => (
                    <Paper
                      key={resource.id}
                      sx={{
                        p: 1.25,
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 1,
                      }}
                    >
                      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={0.5}>
                        <Box flex={1}>
                          <Typography variant="body2" fontWeight={600}>
                            {resource.organization || "Resource"} • {resource.resource_type ? resource.resource_type.replace("_", " ") : "N/A"}
                          </Typography>
                        </Box>
                        <Box display="flex" gap={0.5} alignItems="center">
                          <Chip
                            label={resource.status || "unknown"}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                          {renderMenuButton(resource)}
                        </Box>
                      </Box>
                      {resource.description && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {resource.description.substring(0, 80)}{resource.description.length > 80 ? "..." : ""}
                        </Typography>
                      )}
                      {resource.provided_date && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {new Date(resource.provided_date).toLocaleDateString()}
                        </Typography>
                      )}
                    </Paper>
                  ))}
                </Box>
              </Collapse>

              <Button
                size="small"
                onClick={() => setExpandAll(!expandAll)}
                startIcon={expandAll ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                sx={{ textTransform: "none", mt: 1 }}
              >
                {expandAll ? "View Less" : `View All (${sortedResources.length - 2} more)`}
              </Button>
            </>
          )}
        </>
      ) : (
        <Typography variant="body2" color="text.secondary">
          No resources provided
        </Typography>
      )}
      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={closeMenu}>
        <MenuItem
          onClick={() => {
            const id = activeId;
            closeMenu();
            if (id && onDeleteResource) onDeleteResource(id);
          }}
        >
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} /> Delete
        </MenuItem>
      </Menu>
    </GlassCard>
  );
}

ResourcesCard.propTypes = {
  resources: PropTypes.array,
  onAddResource: PropTypes.func.isRequired,
  onDeleteResource: PropTypes.func,
};

ResourcesCard.defaultProps = {
  resources: [],
};

export default ResourcesCard;
